import Button from '@risksmart-app/components/src/button';
import { useRating } from '@risksmart-app/components/src/hooks/useRating';
import SimpleRatingBadge from '@risksmart-app/components/src/simple-rating-badge/SimpleRatingBadge';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import EmptyEntityCollection from 'src/components/empty-collection/EmptyEntityCollection';
import { useControlTypeLabel } from 'src/pages/assessments/forms/useControlTypeLabel';
import { UNRATED } from 'src/pages/controls/lookupData';
import type { ObjectWithContributors } from 'src/rbac/Permission';
import { Permission } from 'src/rbac/Permission';

import Link from '@/components/link';
import { useRiskScore } from '@/hooks/useRiskScore';
import { useConfigIds } from '@/ratings/useConfigIds';
import { useRiskRatingResolver } from '@/ratings/useRiskRatingResolver';
import type { UseGetTablePropsOptions } from '@/utils/table/hooks/useGetStatelessTableProps';
import { useGetTableProps } from '@/utils/table/hooks/useGetTableProps';
import type { TableFields, TablePropsWithActions } from '@/utils/table/types';
import { dateColumnFromConfig } from '@/utils/table/utils/dateColumn';

import type {
  RiskAssessmentResultFlatFields,
  RiskAssessmentResultRegisterFields,
} from './types';
import { useRiskRatingLabelledFields } from './useRiskRatingLabelledFields';

// Sort `v3` → 3; the '-' placeholder (no config) sorts after all real versions.
const versionSortValue = (v: string) =>
  v === '-' ? Number.MAX_SAFE_INTEGER : Number(v.slice(1));

const useGetFieldConfig = (
  onOpenResult: (id: string) => void,
  riskId: string,
  configIds: string[]
): TableFields<RiskAssessmentResultRegisterFields> => {
  const { t: at } = useTranslation(['common'], {
    keyPrefix: 'assessments',
  });
  const { t: ar } = useTranslation(['common'], {
    keyPrefix: 'assessmentResults',
  });
  const { t: rt } = useTranslation(['common'], {
    keyPrefix: 'ratings',
  });
  const getControlTypeLabel = useControlTypeLabel();
  const riskScores = useRiskScore(riskId);
  const { getByValue: statusGetByValue } = useRating('assessment_status');
  const {
    resolveRiskRating,
    resolveImpact,
    resolveLikelihood,
    hasScoringSettings,
  } = useRiskRatingResolver('standard', {
    configType: 'risk_assessment',
    configIds,
  });

  return useMemo(
    () => ({
      TestDate: dateColumnFromConfig({
        header: { header: rt('columns.TestDate') },
        dateField: 'TestDate',
        onClick: (item) => onOpenResult(item.Id),
      }),
      ParentTitle: {
        header: rt('columns.AssessmentTitle'),
        cell: (item) =>
          item.parents.find((p) => p.assessment)?.assessment ? (
            <Link
              isRelativeUrl={true}
              variant={'secondary'}
              href={item.parents.find((p) => p.assessment)?.assessment?.Id}
            >
              {item.parents.find((p) => p.assessment)?.assessment?.Title}
            </Link>
          ) : (
            '-'
          ),
        isRowHeader: true,
      },
      Methodology: {
        header: rt('columns.Methodology'),
        sortingField: 'Methodology',
        cell: (item) => (
          <SimpleRatingBadge
            rating={{
              label: item.Methodology,
              color: 'light-grey',
            }}
          />
        ),
        exportVal: (item) => item.Methodology,
        filterOptions: {
          filteringOptions: [
            {
              value: rt('methodology.impactLikelihood'),
              label: rt('methodology.impactLikelihood'),
            },
            {
              value: rt('methodology.multiImpact'),
              label: rt('methodology.multiImpact'),
            },
          ],
        },
      },
      ...(hasScoringSettings
        ? {
            Version: {
              header: rt('columns.Version'),
              cell: (item) => (
                <SimpleRatingBadge
                  rating={{ label: item.Version, color: 'light-grey' }}
                />
              ),
              exportVal: (item) => item.Version,
              sortingComparator: (a, b) =>
                versionSortValue(a.Version) - versionSortValue(b.Version),
            },
          }
        : {}),
      ControlTypeLabelled: {
        header: ar('fields.ControlType'),
        cell: (item) => getControlTypeLabel(item.ControlType),
        sortingField: 'ControlType',
      },
      RatingLabelled: {
        header: rt('columns.Rating'),
        sortingField: 'Rating',
        cell: (item) => {
          if (!riskScores.showScore) {
            const resolved = resolveRiskRating({
              likelihood: item.Likelihood,
              impact: item.Impact,
              controlType: item.ControlType,
              rating: item.Rating,
              configId: item.ConfigId,
            });

            return <SimpleRatingBadge rating={resolved ?? UNRATED} />;
          }

          return '-';
        },
      },
      ImpactLabelled: {
        header: rt('columns.Impact'),
        cell: (item) =>
          item.Impact ? (
            <SimpleRatingBadge
              rating={resolveImpact(item.Impact, item.ConfigId) ?? UNRATED}
            />
          ) : (
            '-'
          ),
      },
      LikelihoodLabelled: {
        header: rt('columns.Likelihood'),
        cell: (item) =>
          item.Likelihood ? (
            <SimpleRatingBadge
              rating={
                resolveLikelihood(item.Likelihood, item.ConfigId) ?? UNRATED
              }
            />
          ) : (
            '-'
          ),
      },
      StatusLabelled: {
        header: rt('columns.AssessmentStatus'),
        cell: (item) => {
          const status = item.parents.find((p) => p.assessment)?.assessment
            ?.Status;

          return <SimpleRatingBadge rating={statusGetByValue(status)} />;
        },
      },
      CompletionDate: dateColumnFromConfig({
        header: { header: at('columns.CompletionDate') },
        dateField: 'ActualCompletionDate',
      }),
    }),
    [
      ar,
      at,
      getControlTypeLabel,
      hasScoringSettings,
      onOpenResult,
      resolveImpact,
      resolveLikelihood,
      resolveRiskRating,
      riskScores.showScore,
      rt,
      statusGetByValue,
    ]
  );
};

const useGetRiskRatingTableProps = (
  risk: ObjectWithContributors,
  onOpenResult: (id: string) => void,
  records: RiskAssessmentResultFlatFields[] | undefined
): UseGetTablePropsOptions<RiskAssessmentResultRegisterFields> => {
  const configIds = useConfigIds(records);

  const labelledFields = useRiskRatingLabelledFields(
    risk.Id,
    records,
    configIds
  );
  const fields = useGetFieldConfig(onOpenResult, risk.Id, configIds);

  return {
    customAttributeFormIds: [
      'uncontrolled_risk_assessment_result',
      'controlled_risk_assessment_result',
    ],
    data: labelledFields,
    enableFiltering: true,
    entityLabel: 'rating',
    defaultSortingState: {
      sortingColumn: 'TestDate',
      sortingDirection: 'desc',
    },
    initialColumns: [
      'TestDate',
      'ParentTitle',
      'Methodology',
      'ControlTypeLabelled',
      'RatingLabelled',
      'ImpactLabelled',
      'LikelihoodLabelled',
      'StatusLabelled',
    ],
    preferencesStorageKey: 'RiskRatingRegisterTable-Preferences',
    fields,
  };
};

export const useGetCollectionTableProps = (
  risk: ObjectWithContributors,
  onOpenResult: (id: string) => void,
  onAddRating: () => void,
  records: RiskAssessmentResultFlatFields[] | undefined
): TablePropsWithActions<RiskAssessmentResultRegisterFields> => {
  const { t } = useTranslation(['common']);
  const props = useGetRiskRatingTableProps(risk, onOpenResult, records);

  return {
    ...useGetTableProps(props),
    empty: (
      <EmptyEntityCollection
        entityLabel={t('rating')}
        action={
          <Permission
            permission={'insert:risk_assessment_result'}
            parentObject={risk}
          >
            <Button formAction={'none'} onClick={onAddRating}>
              {t('assessments.add_rating_button')}
            </Button>
          </Permission>
        }
      />
    ),
  };
};
